import "./auth-pages.scss";

import { Field, Form, Formik } from "formik";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { object, string } from "yup";

import { MFALoginSend } from "pwapi";

import { new_login, mfaSelectMethod } from "shared/actions/authActions";
import { mfaLoginFormProps, useMfaLoginForm } from "shared/hooks/mfaHooks";

import {
  PathologyWatchButton as Button,
  BorderlessButton,
} from "components/customInputs/buttons/Button";
import TextInput from "components/customInputs/TextInput";
import MFACodeInput from "components/MFACodeInput";
import dotLogoSrc from "images/PathologyWatch_Dot_Logo_w Pw.png";
import { AppDispatch } from "store/store";

import ToastList from "../toasts/ToastList";

let schema = object().shape({
  username: string().required(),
  password: string().required().min(8),
});

// Errors come for new_password1, new_password2

export const extractErrorMessages = (errors: any, key: string) => {
  if (errors && errors[key] && errors[key].length > 0) {
    return (
      <div>
        {errors[key].map((e: string) => (
          <span>{e}</span>
        ))}
      </div>
    );
  }
  return "";
};

const LoginForm = ({
  isSubmitting,
  errors,
  touched,
  handleChange,
  handleSubmit,
  handleReset,
  values,
  setFieldValue,
}: mfaLoginFormProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    authErrors,
    mfaCodeRequired,
    mfaMethod,
    sendMfaCode,
    reset,
    canResend,
    selectMfaMethod,
    mfaMethodSentByText,
  } = useMfaLoginForm({
    touched,
    values,
    handleReset,
    setFieldValue,
    handleSubmit,
  });

  return (
    <>
      <Form onSubmit={handleSubmit} translate="yes">
        {authErrors.non_field_errors || authErrors.detail ? (
          <div className="alarm alarm-red">
            {authErrors.non_field_errors || authErrors.detail}
          </div>
        ) : (
          ""
        )}
        <Field name="username">
          {({ field }: any) => (
            <TextInput
              required
              id="username"
              placeholder="Username"
              {...field}
              onChange={handleChange}
              className="text-form-field"
              disabled={mfaCodeRequired}
            />
          )}
        </Field>
        {errors && errors.username && touched.username && (
          <div className="formik-error">{errors.username}</div>
        )}
        {!mfaCodeRequired ? (
          <>
            <Field name="password">
              {({ field }: any) => (
                <TextInput
                  required
                  type="password"
                  id="password"
                  placeholder="Password"
                  {...field}
                  onChange={handleChange}
                  className="text-form-field"
                />
              )}
            </Field>
            {errors && errors.password && touched.password && (
              <div className="formik-error">{errors.password}</div>
            )}
          </>
        ) : !mfaMethod ? (
          <div>
            <em className="d-inline-block mb-2">
              Pick an authentication method:
            </em>
            <div className="mb-4">
              <Button
                title="Email"
                className="mx-2"
                color="blue"
                size="large"
                onClick={() => selectMfaMethod(MFALoginSend.mfa_method.EMAIL)}
              >
                Email
              </Button>
              <Button
                title="SMS (Text Message)"
                className="mx-2"
                color="blue"
                size="large"
                onClick={() => selectMfaMethod(MFALoginSend.mfa_method.SMS)}
              >
                SMS
              </Button>
            </div>
            {mfaCodeRequired && (
              <div>
                <BorderlessButton onClick={() => reset()}>
                  <u>CANCEL</u>
                </BorderlessButton>
              </div>
            )}
          </div>
        ) : (
          <div>
            <em className="d-block mb-2">
              Enter the authentication code {mfaMethodSentByText[mfaMethod]}
            </em>
            <Field name="mfa_code">
              {({ field }: any) => (
                <MFACodeInput
                  value={field.value || ""}
                  onChange={(value: string) => setFieldValue("mfa_code", value)}
                />
              )}
            </Field>
          </div>
        )}
        {(!!mfaMethod || !mfaCodeRequired) && (
          <div>
            {mfaCodeRequired ? (
              <>
                <BorderlessButton
                  className="mfa-button"
                  onClick={() => reset()}
                >
                  <u>CANCEL</u>
                </BorderlessButton>
                {mfaMethod && mfaMethod !== "app" && (
                  <BorderlessButton
                    className="mfa-button"
                    onClick={sendMfaCode}
                    disabled={!canResend}
                  >
                    <u>RESEND</u>
                  </BorderlessButton>
                )}
              </>
            ) : (
              <Button
                type="submit"
                title="Log In"
                color="blue"
                disabled={isSubmitting}
                size="large"
              >
                LOG IN
              </Button>
            )}
          </div>
        )}
      </Form>
      {(!!mfaMethod || !mfaCodeRequired) && (
        <div className="trouble_logging_in">
          <span>Having trouble logging in? </span>
          {!mfaCodeRequired ? (
            <Link className="password-reset-link" to="/password-reset">
              Reset your password.
            </Link>
          ) : (
            <BorderlessButton
              onClick={() => dispatch(mfaSelectMethod(undefined))}
            >
              <u>Change your authentication method.</u>
            </BorderlessButton>
          )}
        </div>
      )}
    </>
  );
};

const LoginPage = () => {
  const dispatch = useDispatch<AppDispatch>();

  return (
    <>
      <ToastList />
      {/* Specifying height: 100vh; in CSS doesn't work right on mobile devices with Chrome or Safari,
            because they don't take the address bar height into account.
            Ref https://chanind.github.io/javascript/2019/09/28/avoid-100vh-on-mobile-web.html */}
      <main className="auth-pages" style={{ maxHeight: window.innerHeight }}>
        {/*<span style={{position:'absolute', top: 0, left: '10px'}}>{window.innerWidth}&nbsp;x&nbsp;{window.innerHeight}</span>*/}
        <Link to="/">
          <figure className="DotLogo">
            <img src={dotLogoSrc} alt="dot. by PathologyWatch" />
          </figure>
        </Link>
        <div className="form-div">
          <Formik
            initialValues={{ username: "", password: "" }}
            validationSchema={schema}
            onSubmit={(values, { setSubmitting }) => {
              dispatch(new_login(values));
              setSubmitting(false);
            }}
          >
            {(props) => <LoginForm {...props} />}
          </Formik>
        </div>
        <p>
          PathologyWatch uses a digital software platform independently
          validated as a laboratory-developed test. The systems referenced are
          currently neither FDA cleared nor approved for primary diagnosis.
        </p>
        <footer>
          <a href="mailto:support@pathologywatch.com">Help</a>
          &nbsp;&ndash;&nbsp;
          <a href="https://pathologywatch.com">About&nbsp;Us</a>
        </footer>
      </main>
    </>
  );
};

export default LoginPage;
